import React, { useState, useMemo, useEffect } from "react";
import { formatCurrency } from "../../../../utils/formatting";

interface ProtectedSystemsSelectorProps {
  eligibleSystems: any[];
  getSystemTonnage: (id: string) => number;
  onConfirm: (protectedIds: string[], protectedTonnage: number) => void;
  initialSelected?: string[];
  onCancel?: () => void;
}

const BULKHEAD_COST_PER_TON = 0.2 * 1_000_000; // Cr200,000 per ton
const BULKHEAD_TONNAGE_PERCENT = 0.1; // 10%

const ProtectedSystemsSelector: React.FC<ProtectedSystemsSelectorProps> = ({
  eligibleSystems,
  getSystemTonnage,
  onConfirm,
  initialSelected = [],
  onCancel,
}) => {
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [showError, setShowError] = useState(false);

  // Sync local selected state with initialSelected prop
  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  const systemOptions = useMemo(() => eligibleSystems, [eligibleSystems]);

  const protectedTonnage = useMemo(
    () => selected.reduce((sum, id) => sum + getSystemTonnage(id), 0),
    [selected, getSystemTonnage],
  );
  const cost = useMemo(
    () => protectedTonnage * BULKHEAD_COST_PER_TON,
    [protectedTonnage],
  );
  const bulkheadTonnage = useMemo(
    () => protectedTonnage * BULKHEAD_TONNAGE_PERCENT,
    [protectedTonnage],
  );

  const handleToggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
    setShowError(false);
  };

  const handleConfirm = () => {
    if (selected.length === 0) {
      setShowError(true);
      return;
    }
    onConfirm(selected, protectedTonnage);
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
      <h4 className="text-md font-semibold mb-2">
        Which systems do you want to protect?
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {systemOptions.map((sys) => (
          <label
            key={sys.id}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.includes(sys.id)}
              onChange={() => handleToggle(sys.id)}
              className="form-checkbox h-4 w-4 text-blue-600"
            />
            <span>
              {sys.name}{" "}
              <span className="text-xs text-gray-500">
                ({getSystemTonnage(sys.id)} tons)
              </span>
            </span>
          </label>
        ))}
      </div>
      {showError && (
        <div className="text-red-600 text-sm mb-2">
          Select at least one system to protect.
        </div>
      )}
      {/* Live summary of selected systems and totals */}
      <div className="mb-4 p-2 bg-blue-50 rounded text-sm">
        <b>Summary:</b>{" "}
        {selected.length === 0 ? (
          "No systems selected."
        ) : (
          <>
            Protecting <b>{selected.length}</b> system
            {selected.length > 1 ? "s" : ""}:{" "}
            {selected
              .map((id) => {
                const sys = systemOptions.find((s) => s.id === id);
                return sys ? sys.name : id;
              })
              .join(", ")}
            <br />
            Total Protected Tonnage: <b>{protectedTonnage}</b> tons | Bulkhead
            Tonnage: <b>{bulkheadTonnage.toFixed(1)}</b> tons | Cost:{" "}
            <b>{formatCurrency(cost)}</b>
          </>
        )}
      </div>
      <div className="flex gap-2 mt-4">
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          onClick={handleConfirm}
        >
          Confirm Protection
        </button>
        {onCancel && (
          <button
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default ProtectedSystemsSelector;
